import { ChangeEvent, useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import {
  isEqual, isNumber, omit,
} from 'lodash';
import { useRouter } from 'next/router';
import { EngagementCriteria } from 'lib/types/filters';
import {
  clearQuery, updateRouterQuery, toSlug, slugToValues,
} from 'lib/utils/filterUtils';
import { RoleTagSelect } from 'components/RoleTagSelect/RoleTagSelect';
import { RoleTag } from 'lib/types';
import { useCurrentUser } from 'lib/contexts/ApplicationState';
import { userIsAdmin } from 'lib/helpers';
import { FilterPopover } from 'components/FilterPopover';

interface Props {
  engagement: EngagementCriteria;
  includeUncredited?: string;
  roleTagSlugs: string[];
  disabled?: boolean;
}

export const MoreFilter = ({
  engagement,
  includeUncredited,
  roleTagSlugs,
  disabled = false,
}: Props) => {
  const router = useRouter();
  const [roleTags, setRoleTags] = useState<RoleTag[]>([]);
  /* keeping this for changes locally */
  const [values, setValues] = useState<EngagementCriteria>(engagement);
  const [_roleTags, setLocalRoleTags] = useState<RoleTag[]>([]);
  const currentUser = useCurrentUser();

  useEffect(() => {
    setValues(engagement);
  }, [engagement]);

  useEffect(() => {
    if (!roleTagSlugs) {
      return;
    }

    const rt = roleTagSlugs.map((r) => {
      const { id, label: name } = slugToValues(r);
      return {
        id,
        name,
      } as RoleTag;
    });
    setRoleTags(rt);
    setLocalRoleTags(rt);
  }, [roleTagSlugs]);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const targetValue = parseInt(event.target.value, 10);
    const isValidValue = isNumber(targetValue) && targetValue >= 0;
    if (isValidValue) {
      setValues({
        ...values,
        [event.target.name]: targetValue,
      });
      return;
    }

    setValues(omit(values, event.target.name));
  };

  const onApply = () => {
    const defaultCriteriaIfUncreditedOrSuggestedCredits = () => {
      // if fileType already selected
      if (router.query.fileType) return {};

      // if uncredited, select default file types
      return { fileType: ['VIDEO', 'GIF', 'IMAGE'] };
    };

    if (!isEqual(engagement, values)
      || !isEqual(roleTags, _roleTags)) {
      const q = Object.keys(values).reduce(
        (acc, key) => {
          acc[key] = values[key as keyof EngagementCriteria];
          return acc;
        }, {} as any,
      );

      const slugs = _roleTags.map((r: any) => toSlug(r.name, r.id));
      const query = {
        ...clearQuery(Object.keys(engagement)),
        ...defaultCriteriaIfUncreditedOrSuggestedCredits(),
        ...q,
        roleTags: slugs,
      };
      updateRouterQuery(router, query);
    }
  };

  const onClear = () => {
    setValues({});
    setRoleTags([]);
    setLocalRoleTags([]);

    const q = Object.keys(engagement).reduce(
      (acc, key) => {
        acc[key] = [];
        return acc;
      }, {} as any,
    );
    updateRouterQuery(router, {
      ...q, includeUncredited: [], roleTags: [], creditedStatus: [], sort: [],
    });
  };

  const getTitle = () => {
    const keys = Object.keys(engagement).length;
    const uncreditedCount = includeUncredited === 'Y' ? 1 : 0;
    const tags = roleTags.length > 0 ? 1 : 0;
    const count = keys + uncreditedCount + tags;
    if (count > 0) {
      return `More (${count})`;
    }
    return 'More';
  };

  const renderMinMax = (
    metricName: string,
    metricMinKey: keyof EngagementCriteria,
    metricMaxKey: keyof EngagementCriteria,
  ) => (
    <div className="mb-2 items-center">
      <h6 className="font-semibold mb-1">
        {metricName}
      </h6>
      <div className="flex gap-4 justify-between">
        <TextField
          label="min"
          variant="outlined"
          type="number"
          InputProps={{ inputProps: { min: 0 } }}
          name={metricMinKey}
          onChange={onChange}
          className="w-full"
          value={values[metricMinKey] || ''}
          size="small"
        />
        <TextField
          label="max"
          variant="outlined"
          type="number"
          InputProps={{ inputProps: { min: 0 } }}
          name={metricMaxKey}
          onChange={onChange}
          className="w-full"
          value={values[metricMaxKey] || ''}
          size="small"
        />
      </div>
    </div>
  );

  return (
    <FilterPopover
      title={getTitle()}
      onApply={onApply}
      onClear={onClear}
      clearable={Object.keys(values).length > 0
        || _roleTags.length > 0}
      disabled={disabled}
    >
      <>
        {renderMinMax('Views', 'minViews', 'maxViews')}
        {renderMinMax('Comments', 'minComments', 'maxComments')}
        {renderMinMax('Shares', 'minShares', 'maxShares')}
        {renderMinMax('Likes', 'minLikes', 'maxLikes')}

        {/* Admin only for now */}
        {userIsAdmin(currentUser) && (
          <RoleTagSelect
            label="Role Categories"
            onChange={(e) => { setLocalRoleTags(e as RoleTag[]); }}
            value={_roleTags}
            size="small"
          />
        )}
      </>
    </FilterPopover>
  );
};
